import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, Col, Row } from 'reactstrap';
import { API_URL, baseUrl } from '../../api';
import {
    getAgreementsListReq,
    getClientWithIdReq,
} from '../../service/clientService';
import { changePreloader, setBreadcrumbItems } from '../../store/actions';
import { formatDate } from '../../utility/formatDate';

const PastAgreements = (props) => {
  document.title = 'Past Agreements';
  const navigate = useNavigate();
  const userType =
    JSON.parse(localStorage.getItem('user'))?.userType || 'client';
  const dispatch = useDispatch();
  const { clientId } = useParams();
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [paginationPageSize, setPaginationPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [clientName, setClientName] = useState('');

  const handleDownload = async (agreement) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (agreement.document) {
        const url = `${baseUrl}/${API_URL.viewFile}/agreements/${agreement.document}`;
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.status === 200) {
          const signedUrl = response.data.url;
          window.open(signedUrl, '_blank');
        }
      } else {
        toast.error('Agreement document not available');
      }
    } catch (error) {
      console.error('Error downloading agreement:', error);
      toast.error('Failed to download agreement');
    }
  };

  useEffect(() => {
    if (!clientName) return;

    let breadcrumbs = [];

    if (userType === 'admin') {
      breadcrumbs = [
        { title: 'Dashboard', link: '/dashboard' },
        { title: 'Clients', link: '/clients' },
        { title: clientName, link: `/client/${clientId}` },
        { title: 'Past Agreements', link: '#' },
      ];
    } else {
      breadcrumbs = [
        { title: 'Dashboard', link: '/dashboard' },
        { title: 'Past Agreements', link: '#' },
      ];
    }

    props.setBreadcrumbItems('Past Agreements', breadcrumbs);
  }, [clientName, userType]);

  const columnDefs = [
    {
      headerName: 'Agreement ID',
      field: '_id',
      tooltipField: '_id',
      suppressMenu: true,
      minWidth: 200,
      floatingFilterComponentParams: { suppressFilterButton: true },
      cellRenderer: (params) => {
        return params.value || '-';
      },
    },
    {
      headerName: 'Created Date',
      field: 'createdAt',
      minWidth: 150,
      suppressMenu: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
      cellRenderer: (params) => {
        if (params.value) {
          const date = new Date(params.value);
          return <>{formatDate(date)}</>;
        }
        return '-';
      },
    },
    {
      headerName: 'Validity',
      field: 'validity',
      minWidth: 150,
      suppressMenu: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
      cellRenderer: (params) => {
        if (params.value) {
          const date = new Date(params.value);
          return <>{formatDate(date)}</>;
        }
        return '-';
      },
    },
    {
      headerName: 'Action',
      field: 'action',
      sortable: false,
      width: 150,
      cellClass: 'actions-button-cell',
      cellRenderer: (params) => {
        return (
          <Button
            color="primary"
            size="sm"
            onClick={() => handleDownload(params.data)}
          >
            <i className="mdi mdi-download mx-1"></i>
            Download
          </Button>
        );
      },
      suppressMenu: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
    },
  ];

  const autoSizeStrategy = {
    type: 'fitGridWidth',
  };

  const pagination = true;
  const paginationPageSizeSelector = [25, 50, 100];

  const getClientData = async () => {
    try {
      const data = { _id: clientId };
      const res = await getClientWithIdReq(data);
      if (res.payload?.client) {
        setClientName(res.payload.client.name);
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
    }
  };

  const getListOfRowData = useCallback(async () => {
    if (rowData[(page - 1) * paginationPageSize]) {
      return;
    }
    dispatch(changePreloader(true));
    try {
      const body = {
        clientId: clientId,
        page: page,
        limit: paginationPageSize,
      };
      const response = await getAgreementsListReq(body);
      const agreements = response?.payload?.list || [];

      const emptyObjects = Array.from(
        { length: paginationPageSize },
        () => null,
      );
      let filledRows;

      if (agreements.length < paginationPageSize) {
        filledRows = [...agreements];
      } else {
        filledRows = [...agreements, ...emptyObjects];
      }

      const newData = [...rowData];
      newData.splice(
        (page - 1) * paginationPageSize,
        paginationPageSize,
        ...filledRows,
      );
      setRowData(newData);
    } catch (error) {
      console.error('Error fetching agreements:', error);
      toast.error('Failed to fetch agreements');
    } finally {
      dispatch(changePreloader(false));
    }
  }, [page, paginationPageSize, clientId]);

  useEffect(() => {
    getClientData();
  }, [clientId]);

  useEffect(() => {
    if (clientId) {
      getListOfRowData();
    }
  }, [page, paginationPageSize, clientId]);

  const onPaginationChanged = useCallback((event) => {
    let pageSize = gridRef.current.api.paginationGetPageSize();
    setPaginationPageSize(pageSize);

    if (pageSize !== paginationPageSize) {
      setPaginationPageSize(pageSize);
    }
    const newPage = gridRef.current.api.paginationGetCurrentPage() + 1;

    if (page !== newPage) {
      setPage(newPage);
    }
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <div style={{ height: '600px', width: '100%' }}>
                    <AgGridReact
                      ref={gridRef}
                      rowData={rowData}
                      columnDefs={columnDefs}
                      defaultColDef={{
                        sortable: true,
                        resizable: true,
                        floatingFilter: false,
                      }}
                      autoSizeStrategy={autoSizeStrategy}
                      pagination={pagination}
                      paginationPageSize={paginationPageSize}
                      paginationPageSizeSelector={paginationPageSizeSelector}
                      onPaginationChanged={onPaginationChanged}
                      domLayout="normal"
                      className="ag-theme-quartz"
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  return {};
};

export default connect(mapStateToProps, { setBreadcrumbItems })(PastAgreements);
